import { useCallback, useEffect, useRef, useState } from "react";
import api, { unwrap, apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import PostCard from "@/components/PostCard";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Generic paginated feed component.
 * @param {(page, perPage) => string} endpointFn - function returning URL for a page.
 * @param {string} testid
 * @param {ReactNode} emptyState
 * @param {boolean} fallbackToOwnPosts - when the primary endpoint returns
 *   zero items on page 1, backfill the feed with the current user's own
 *   posts so the surface never looks broken (Xano's home/discover queries
 *   sometimes exclude the viewer's own author_id).
 */
export default function FeedList({ endpointFn, fetchPageFn, testid = "feed-list", emptyState, header, fallbackToOwnPosts = true }) {
  const { user } = useAuth();
  const [pages, setPages] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [err, setErr] = useState("");
  const sentinelRef = useRef(null);
  // Monotonic request-generation id — every time the effect re-runs (e.g.,
  // because `user` hydrated asynchronously after login) we bump this so any
  // in-flight fetchPage from a previous generation is discarded without
  // mutating shared state.
  const genRef = useRef(0);

  // Hydrate `author` on posts that don't inline it. Xano's feed/search/
  // profile-posts responses return only `user_id` — the frontend fills the
  // gap using the current viewer as a stable source.
  const hydrateAuthor = useCallback(
    (p) => {
      if (p.author || p.user) return p;
      if (user && p.user_id === user.id) {
        return {
          ...p,
          author: {
            id: user.id,
            username: user.username,
            display_name: user.display_name,
            profile_photo_url: user.profile_photo_url,
            avatar_url: user.avatar_url,
            verified: user.is_verified,
          },
        };
      }
      return p;
    },
    [user]
  );

  const fetchPage = useCallback(
    async (p, gen) => {
      setLoading(true);
      setErr("");
      try {
        // Prefer a caller-supplied fetch function (used by Home to merge
        // home + following feeds).  Falls back to the single-endpoint mode.
        let items = [];
        let nextPageHint = null;
        if (fetchPageFn) {
          const out = await fetchPageFn(p, 10);
          if (gen !== genRef.current) return;
          items = Array.isArray(out?.items) ? out.items : [];
          nextPageHint = out?.nextPage ?? null;
        } else {
          const res = await api.get(endpointFn(p, 10));
          if (gen !== genRef.current) return;
          const data = unwrap(res.data);
          if (Array.isArray(data)) items = data;
          else if (Array.isArray(data?.items)) items = data.items;
          else if (Array.isArray(data?.posts)) items = data.posts;
          else if (Array.isArray(data?.results)) items = data.results;
          else if (Array.isArray(data?.data)) items = data.data;
        }

        // Fallback: if the primary endpoint returned nothing on the first
        // page AND the viewer is known, pull their own recent posts so the
        // feed shows content instead of a stark empty state.
        if (p === 1 && items.length === 0 && fallbackToOwnPosts && user?.username) {
          try {
            const res2 = await api.get(EP.profilePostsByUsername(user.username, 1, 10));
            if (gen !== genRef.current) return;
            const d2 = unwrap(res2.data);
            const own = d2?.items || d2?.posts || (Array.isArray(d2) ? d2 : []);
            items = own;
          } catch { /* keep items empty */ }
        }

        const hydrated = items.map(hydrateAuthor);

        // Dedupe by id INSIDE the setter — the previous approach used a
        // useRef Set that got reset on every effect re-run, which race'd
        // against in-flight fetches and could accidentally drop every item.
        setPages((prev) => {
          if (p === 1) return hydrated;
          const seen = new Set(prev.map((it) => it.id ?? it.post_id));
          const fresh = hydrated.filter((it) => {
            const id = it.id ?? it.post_id;
            if (id == null) return true;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
          });
          return [...prev, ...fresh];
        });
        setHasMore(fetchPageFn ? nextPageHint !== null : items.length >= 10);
      } catch (e) {
        if (gen !== genRef.current) return;
        setErr(apiError(e));
        setHasMore(false);
      } finally {
        if (gen === genRef.current) {
          setLoading(false);
          setInitialLoading(false);
        }
      }
    },
    [endpointFn, fetchPageFn, hydrateAuthor, fallbackToOwnPosts, user]
  );

  useEffect(() => {
    // Bump generation — any in-flight fetch from the previous gen will
    // short-circuit (see gen check inside fetchPage).
    genRef.current += 1;
    const myGen = genRef.current;
    setPages([]);
    setPage(1);
    setHasMore(true);
    setInitialLoading(true);
    fetchPage(1, myGen);
  }, [fetchPage]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const next = page + 1;
          setPage(next);
          fetchPage(next, genRef.current);
        }
      },
      { rootMargin: "400px" }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [page, hasMore, loading, fetchPage]);

  const removePost = (id) => {
    setPages((prev) => prev.filter((p) => (p.id ?? p.post_id) !== id));
  };

  return (
    <div data-testid={testid}>
      {header}
      {initialLoading && (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-3xl border border-border p-6">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-foreground/5 animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-32 bg-foreground/5 rounded animate-pulse" />
                  <div className="h-2 w-20 bg-foreground/5 rounded animate-pulse" />
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <div className="h-3 w-full bg-foreground/5 rounded animate-pulse" />
                <div className="h-3 w-3/4 bg-foreground/5 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!initialLoading && pages.length === 0 && !err && (emptyState || (
        <div className="rounded-3xl border border-border p-10 text-center text-muted-foreground" data-testid={`${testid}-empty`}>
          Nothing here yet.
        </div>
      ))}

      {err && !initialLoading && pages.length === 0 && (
        <div className="rounded-3xl border border-destructive/40 bg-destructive/10 text-destructive text-sm p-6" data-testid={`${testid}-error`}>
          {err}
          <button className="ml-4 underline underline-offset-4" onClick={() => fetchPage(1, genRef.current)}>Retry</button>
        </div>
      )}

      <div className="divide-y divide-border/60">
        {pages.map((p) => (
          <PostCard key={p.id ?? p.post_id} post={p} onDeleted={removePost} />
        ))}
      </div>

      {hasMore && !initialLoading && (
        <div ref={sentinelRef} className="py-10 flex justify-center text-muted-foreground text-sm">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <span>Loading more…</span>}
        </div>
      )}
    </div>
  );
}
